import {Directive, ElementRef, HostListener, inject, Input, OnInit, Renderer2} from '@angular/core';
import {NgControl} from '@angular/forms';
import {VatFormatterService} from "./service/vat-formatter.service";

@Directive({
  selector: '[ibpVatAutoFormat]',
  standalone: true
})
export class IbpVatAutoFormatDirective implements OnInit {
  private el: ElementRef = inject(ElementRef);
  private renderer: Renderer2 = inject(Renderer2);
  private ngControl: NgControl = inject(NgControl);
  private vatFormatter: VatFormatterService = inject(VatFormatterService);

  @Input() set vatCountry(vatCountry: string) {
    if(this.ngControl?.control?.value) {
      this.updateCountry(vatCountry);
    }
    this._vatCountry = vatCountry;
  }

  get vatCountry() {
    return this._vatCountry;
  }
  private _vatCountry: string = '';

  ngOnInit(): void {
    this.updateCountry(this.vatCountry);

    if (this.vatCountry) {
      this.setInitialValue();
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    if (!this.vatCountry || !event.target) return;

    if (!this.isCountrySupported()) return;

    const inputValue = event.target.value;
    const formatted = this.vatFormatter.formatAsUserTypes(inputValue, this.vatCountry);

    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(formatted, { emitEvent: false });
    }

    if (event.target.value !== formatted) {
      event.target.value = formatted;
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: any): void {
    if (!this.vatCountry) return;

    if (!this.isCountrySupported()) return;

    event.preventDefault();
    const pastedText = (event.clipboardData || (window as any).clipboardData).getData('text');
    const formatted = this.vatFormatter.formatComplete(pastedText, this.vatCountry);

    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(formatted);
    }

    event.target.value = formatted;
  }

  @HostListener('blur', ['$event'])
  onBlur(event: any): void {
    if (!this.vatCountry || !event.target) return;

    if (!this.isCountrySupported()) return;

    const formatted = this.vatFormatter.formatComplete(event.target.value, this.vatCountry);

    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(formatted);
    }

    event.target.value = formatted;
  }

  private updatePlaceholder(): void {
    if (!this.vatCountry) return;

    const isSupported = this.isCountrySupported();
    const placeholder = isSupported
      ? this.vatFormatter.getPlaceholder(this.vatCountry)
      : 'Enter VAT number manually';

    const maxLength = isSupported
      ? this.vatFormatter.getMaxLength(this.vatCountry)
      : 20;

    this.renderer.setAttribute(this.el.nativeElement, 'placeholder', placeholder);
    this.renderer.setAttribute(this.el.nativeElement, 'maxlength', maxLength.toString());

    const input = this.el.nativeElement;
    input.placeholder = placeholder;
    input.maxLength = maxLength;

    if(this.ngControl?.control) {
      this.ngControl.control.reset();
    }
  }

  private setInitialValue(): void {
    if (this.isCountrySupported() && this.ngControl && this.ngControl.control) {
      const currentValue = this.ngControl.control.value;
      if (!currentValue || currentValue.trim() === '') {
        const initialValue = this.vatFormatter.getInitialValue(this.vatCountry);
        this.ngControl.control.setValue(initialValue);
        this.el.nativeElement.value = initialValue;
      }
    }
  }

  updateCountry(newCountry: string): void {
    const currentValue = this.ngControl?.control?.value || '';
    this._vatCountry = newCountry;
    this.updatePlaceholder();
    const newValue = this.vatFormatter.handleCountryChange(currentValue, newCountry);

    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(newValue);
    }
    this.el.nativeElement.value = newValue;
  }

  private isCountrySupported() {
    return this.vatFormatter.getSupportedCountries()
      .some(c => c.code === this.vatCountry);
  }
}
